// components/CookieConsent.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
  Chip,
  Divider,
  Fade,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Cookie as CookieIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Shield as ShieldIcon,
  BarChart as AnalyticsIcon,
  Campaign as MarketingIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

/* ============================================================
   CONFIG
   ============================================================ */
const CONSENT_KEY = 'qrtable_cookie_consent_v1';
const CONSENT_VERSION = 1;

const DEFAULT_CONSENT = {
  essential: true,          // always true, cannot be disabled
  analytics: false,
  marketing: false,
  version: CONSENT_VERSION,
  timestamp: null,
};

/* ============================================================
   Helpers
   ============================================================ */
export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Reset if version changed
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const hasConsent = (category) => {
  const consent = getCookieConsent();
  if (!consent) return false;
  return !!consent[category];
};

export const saveConsent = (consent) => {
  const payload = {
    ...DEFAULT_CONSENT,
    ...consent,
    essential: true,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn('Failed to save cookie consent:', err);
  }
  applyConsentToServices(payload);
  return payload;
};

export const resetConsent = () => {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {}
};

/**
 * Apply consent to third-party services.
 * Extend this function as you add more trackers.
 */
const applyConsentToServices = (consent) => {
  // Google Analytics / Consent Mode v2
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
    });
  }

  // Meta Pixel
  if (typeof window !== 'undefined' && window.fbq && !consent.marketing) {
    // If FB pixel was loaded, opt out
    if (typeof window.fbq === 'function') {
      try {
        window.fbq('consent', 'revoke');
      } catch {}
    }
  }
};

/* ============================================================
   Component
   ============================================================ */
const CookieConsent = ({ language = 'en' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = language === 'ar';

  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [consent, setConsent] = useState(DEFAULT_CONSENT);

  // Check on mount
  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      // Small delay so it doesn't flash on load
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
    // Already accepted — apply previously stored consent
    applyConsentToServices(existing);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const payload = saveConsent({ essential: true, analytics: true, marketing: true });
    setConsent(payload);
    setVisible(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const payload = saveConsent({ essential: true, analytics: false, marketing: false });
    setConsent(payload);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    const payload = saveConsent(consent);
    setConsent(payload);
    setVisible(false);
  }, [consent]);

  const toggle = (key) => {
    if (key === 'essential') return; // locked
    setConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Translations
  const t = {
    title: isRtl ? 'نحترم خصوصيتك 🍪' : 'We value your privacy 🍪',
    subtitle: isRtl
      ? 'نستخدم ملفات تعريف الارتباط لتخصيص تجربتك وتحسين خدماتنا. يمكنك اختيار ما تسمح به.'
      : 'We use cookies to personalize your experience and improve our services. You choose what to allow.',
    acceptAll: isRtl ? 'قبول الكل' : 'Accept all',
    rejectAll: isRtl ? 'رفض الكل' : 'Reject all',
    customize: isRtl ? 'تخصيص' : 'Customize',
    savePrefs: isRtl ? 'حفظ التفضيلات' : 'Save preferences',
    back: isRtl ? 'رجوع' : 'Back',
    close: isRtl ? 'إغلاق' : 'Close',
    essentialTitle: isRtl ? 'ملفات ضرورية' : 'Essential cookies',
    essentialDesc: isRtl
      ? 'مطلوبة لتشغيل الموقع (تسجيل الدخول، السلة، الأمان). لا يمكن تعطيلها.'
      : 'Required for the site to function (login, cart, security). Cannot be disabled.',
    analyticsTitle: isRtl ? 'تحليلات' : 'Analytics cookies',
    analyticsDesc: isRtl
      ? 'تساعدنا على فهم كيفية استخدام الموقع لتحسين التجربة.'
      : 'Help us understand how the site is used so we can improve it.',
    marketingTitle: isRtl ? 'تسويق' : 'Marketing cookies',
    marketingDesc: isRtl
      ? 'تُستخدم لعرض إعلانات أكثر ملاءمة لك.'
      : 'Used to show more relevant ads to you.',
    alwaysOn: isRtl ? 'مفعّل دائماً' : 'Always on',
    learnMore: isRtl ? 'اعرف المزيد' : 'Learn more',
    privacyPolicy: isRtl ? 'سياسة الخصوصية' : 'Privacy Policy',
    manage: isRtl ? 'إدارة التفضيلات' : 'Manage preferences',
  };

  if (!visible) return null;

  const Transition = isMobile ? Slide : Fade;

  return (
    <Transition
      in={visible}
      direction="up"
      timeout={500}
      appear
    >
      <Box
        dir={isRtl ? 'rtl' : 'ltr'}
        role="dialog"
        aria-modal="false"
        aria-label={t.title}
        sx={{
          position: 'fixed',
          bottom: { xs: 0, sm: 16 },
          [isRtl ? 'right' : 'left']: { xs: 0, sm: 16 },
          [isRtl ? 'left' : 'right']: { xs: 0, sm: 'auto' },
          zIndex: 2000,
          maxWidth: { xs: '100%', sm: 420 },
          width: { xs: '100%', sm: 420 },
          pointerEvents: 'none',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            pointerEvents: 'auto',
            bgcolor: 'rgba(20, 20, 20, 0.96)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid rgba(230, 184, 0, 0.25)',
            borderBottomLeftRadius: { xs: 0, sm: 20 },
            borderBottomRightRadius: { xs: 0, sm: 20 },
            borderTopLeftRadius: { xs: 20, sm: 20 },
            borderTopRightRadius: { xs: 20, sm: 20 },
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(230,184,0,0.05)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
            },
          }}
        >
          {/* Close */}
          <IconButton
            size="small"
            onClick={handleRejectAll}
            aria-label={t.close}
            sx={{
              position: 'absolute',
              top: 10,
              [isRtl ? 'left' : 'right']: 10,
              color: '#8A8A8A',
              zIndex: 2,
              '&:hover': { color: '#FFD700', bgcolor: 'rgba(230,184,0,0.1)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ p: { xs: 2.5, sm: 3 }, pt: 2.5 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5, pr: 3 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  bgcolor: 'rgba(230, 184, 0, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CookieIcon sx={{ color: '#FFD700', fontSize: 22 }} />
              </Box>
              <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#fff', lineHeight: 1.2 }}>
                  {t.title}
                </Typography>
                <Typography sx={{ color: '#8A8A8A', fontSize: '0.7rem', mt: 0.25 }}>
                  QR Table
                </Typography>
              </Box>
            </Stack>

            {/* Description */}
            <Typography
              sx={{
                color: '#B8B8B8',
                fontSize: '0.82rem',
                lineHeight: 1.6,
                mb: 2,
                textAlign: isRtl ? 'right' : 'left',
              }}
            >
              {t.subtitle}{' '}
              <Box
                component="a"
                href="/privacy"
                sx={{
                  color: '#FFD700',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t.privacyPolicy}
              </Box>
            </Typography>

            {/* Customize panel */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2 }} />

                {/* Essential */}
                <ConsentRow
                  icon={<LockIcon sx={{ fontSize: 18 }} />}
                  title={t.essentialTitle}
                  description={t.essentialDesc}
                  checked
                  disabled
                  badge={t.alwaysOn}
                  isRtl={isRtl}
                />

                {/* Analytics */}
                <ConsentRow
                  icon={<AnalyticsIcon sx={{ fontSize: 18 }} />}
                  title={t.analyticsTitle}
                  description={t.analyticsDesc}
                  checked={consent.analytics}
                  onChange={() => toggle('analytics')}
                  isRtl={isRtl}
                />

                {/* Marketing */}
                <ConsentRow
                  icon={<MarketingIcon sx={{ fontSize: 18 }} />}
                  title={t.marketingTitle}
                  description={t.marketingDesc}
                  checked={consent.marketing}
                  onChange={() => toggle('marketing')}
                  isRtl={isRtl}
                />
              </Box>
            </Collapse>

            {/* Actions */}
            {!expanded ? (
              <Stack spacing={1}>
                <Button
                  fullWidth
                  onClick={handleAcceptAll}
                  startIcon={<CheckCircleIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #E6B800, #FFD700)',
                    color: '#111',
                    fontWeight: 800,
                    py: 1.2,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    boxShadow: '0 8px 20px rgba(230, 184, 0, 0.28)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FFD700, #E6B800)',
                      boxShadow: '0 12px 28px rgba(230, 184, 0, 0.45)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {t.acceptAll}
                </Button>

                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    onClick={handleRejectAll}
                    sx={{
                      color: '#B8B8B8',
                      border: '1px solid rgba(255,255,255,0.08)',
                      fontWeight: 700,
                      py: 1.1,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      '&:hover': {
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        borderColor: 'rgba(255,255,255,0.15)',
                      },
                    }}
                  >
                    {t.rejectAll}
                  </Button>
                  <Button
                    fullWidth
                    onClick={() => setExpanded(true)}
                    startIcon={<SettingsIcon />}
                    sx={{
                      color: '#FFD700',
                      border: '1px solid rgba(230, 184, 0, 0.35)',
                      bgcolor: 'rgba(230, 184, 0, 0.08)',
                      fontWeight: 700,
                      py: 1.1,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      '& .MuiButton-startIcon': { mr: isRtl ? 0 : 0.5, ml: isRtl ? 0.5 : 0 },
                      '&:hover': {
                        bgcolor: 'rgba(230, 184, 0, 0.15)',
                        borderColor: 'rgba(230, 184, 0, 0.6)',
                      },
                    }}
                  >
                    {t.customize}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Button
                  fullWidth
                  onClick={handleSavePreferences}
                  startIcon={<CheckCircleIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #E6B800, #FFD700)',
                    color: '#111',
                    fontWeight: 800,
                    py: 1.2,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    boxShadow: '0 8px 20px rgba(230, 184, 0, 0.28)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FFD700, #E6B800)',
                      boxShadow: '0 12px 28px rgba(230, 184, 0, 0.45)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {t.savePrefs}
                </Button>
                <Button
                  fullWidth
                  onClick={() => setExpanded(false)}
                  sx={{
                    color: '#8A8A8A',
                    fontWeight: 600,
                    py: 0.8,
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    '&:hover': { color: '#FFD700', bgcolor: 'transparent' },
                  }}
                >
                  {t.back}
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Transition>
  );
};

/* ============================================================
   Sub-component: ConsentRow
   ============================================================ */
const ConsentRow = ({ icon, title, description, checked, onChange, disabled, badge, isRtl }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: '12px',
        mb: 1,
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.04)',
        transition: 'all 0.2s ease',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            bgcolor: 'rgba(230,184,0,0.1)',
            color: '#FFD700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: isRtl ? 'right' : 'left' }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#fff', fontSize: '0.82rem', fontWeight: 700 }}>
              {title}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(230,184,0,0.15)',
                  color: '#FFD700',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Stack>
          <Typography
            onClick={() => setOpen((v) => !v)}
            sx={{
              color: '#7A7A7A',
              fontSize: '0.7rem',
              cursor: 'pointer',
              mt: 0.25,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              '&:hover': { color: '#B8B8B8' },
            }}
          >
            {open ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
            {isRtl ? 'التفاصيل' : 'Details'}
          </Typography>
        </Box>
        <Switch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          size="small"
          inputProps={{ 'aria-label': title }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              bgcolor: '#E6B800',
              opacity: 0.7,
            },
            '& .MuiSwitch-track': { bgcolor: '#3A3A3A' },
            '& .MuiSwitch-switchBase.Mui-disabled': { color: '#666' },
          }}
        />
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Typography
          sx={{
            color: '#8A8A8A',
            fontSize: '0.72rem',
            lineHeight: 1.5,
            mt: 1,
            pl: isRtl ? 0 : 5.5,
            pr: isRtl ? 5.5 : 0,
          }}
        >
          {description}
        </Typography>
      </Collapse>
    </Box>
  );
};

export default CookieConsent;